// API クライアント — Cloudflare Access 環境では自動的に Cf-Access-Jwt-Assertion が付与される
// 本番では Worker のフルURLを VITE_API_BASE に指定する（Pages と Worker が別ドメインのため）
// 開発時は空文字（Vite proxy が /api を Worker に転送）

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    let errorBody: { error?: string; details?: unknown } = {};
    try {
      errorBody = await res.json();
    } catch {
      errorBody = { error: res.statusText };
    }
    throw new ApiError(errorBody.error ?? `HTTP ${res.status}`, res.status, errorBody.details);
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  // JSON 以外（HTML 等）が返った場合は API に到達していない可能性が高い
  const text = await res.text();
  throw new ApiError(
    `想定外のレスポンス形式（${contentType}）。VITE_API_BASE と Worker の到達性を確認してください。先頭200文字: ${text.slice(0, 200)}`,
    res.status,
  );
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  raw: (path: string, options?: RequestInit) => fetch(`${API_BASE}${path}`, options),
};

export { ApiError };
